import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl-next';

import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import { PoweredByTemplates } from '@confluence/powered-by-templates';
import { ProfileLink } from '@confluence/profile/entry-points/ProfileLinkComponent.ts';
import { ProfileCardWrapper } from '@confluence/profile/entry-points/ProfileCardWrapper.ts';

import { i18n } from './i18n.ts';
import type { UserShape } from './ownership-models.ts';

type PageOwnershipBylineContentProps = {
	owner: UserShape;
	templateId: string | null;
	abbreviateBylineText?: boolean;
	generatedBy?: string | null;
};
export const PageOwnershipBylineContent = ({
	owner,
	templateId,
	abbreviateBylineText,
	generatedBy,
}: PageOwnershipBylineContentProps) => {
	const style = {
		color: token('color.text.subtle'),
	};
	const intl = useIntl();

	const bylineTextTemplate = abbreviateBylineText
		? i18n.abbreviatedBylineMainText
		: i18n.bylineMainTextWithTemplate;

	let bylineTextNonTemplate;
	if (abbreviateBylineText) {
		if (generatedBy === 'ONBOARDING' && fg('cc-activation-onboarding-byline')) {
			bylineTextNonTemplate = i18n.bylineMainTextCreatedByConfluence;
		} else {
			bylineTextNonTemplate = i18n.abbreviatedBylineMainText;
		}
	} else {
		if (generatedBy === 'ONBOARDING' && fg('cc-activation-onboarding-byline')) {
			bylineTextNonTemplate = i18n.bylineMainTextCreatedByConfluence;
		} else {
			bylineTextNonTemplate = i18n.bylineMainText;
		}
	}

	const withTemplate = intl.formatMessage(i18n.poweredByTemplates);
	const ownerLabel = templateId
		? intl.formatMessage(bylineTextTemplate, { user: owner.fullName, withTemplate })
		: intl.formatMessage(bylineTextNonTemplate, { user: owner.fullName });

	const linkOnly = (
		<ProfileLink
			userId={owner.userId}
			fullName={owner.fullName}
			isAnonymous={owner.isAnonymous}
			referralSource="profilecard"
			// eslint-disable-next-line @atlaskit/ui-styling-standard/enforce-style-prop -- Ignored via go/DSP-18766
			style={style}
			ariaLabel={ownerLabel}
		/>
	);
	const profileLink = (
		<ProfileCardWrapper userId={owner.userId} ariaLabel={ownerLabel}>
			{linkOnly}
		</ProfileCardWrapper>
	);

	return templateId ? (
		<FormattedMessage
			{...bylineTextTemplate}
			values={{
				user: profileLink,
				withTemplate: <PoweredByTemplates templateId={templateId} />,
			}}
		/>
	) : (
		<FormattedMessage {...bylineTextNonTemplate} values={{ user: profileLink }} />
	);
};
